import React, { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

const CountUp = ({
  from = 0,
  to = 100,
  duration = 1,
  separator = ",",
  decimals = 0,
  decimal = ".",
  direction = "up",
  className = "",
  prefix = "",
  suffix = "",
}) => {
  const [count, setCount] = useState(direction === "up" ? from : to);
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });
  
  const startTimeRef = useRef(null);
  const frameRef = useRef(null);
  const prevCountRef = useRef(count);
  
  // Format number with separators and decimals
  const formatNumber = (num) => {
    const fixedNumber = parseFloat(num).toFixed(decimals);
    const [wholeNumber, decimalPart] = fixedNumber.split('.');
    
    // Add separator to whole number
    const parts = [];
    let counter = 0;
    
    for (let i = wholeNumber.length - 1; i >= 0; i--) {
      counter++;
      parts.unshift(wholeNumber[i]);
      if (counter % 3 === 0 && i !== 0) {
        parts.unshift(separator);
      }
    }
    
    const formattedWhole = parts.join('');
    
    // Return with decimals if needed
    return decimals 
      ? `${prefix}${formattedWhole}${decimal}${decimalPart}${suffix}` 
      : `${prefix}${formattedWhole}${suffix}`;
  };
  
  useEffect(() => {
    if (!inView) return;
    
    // Reset reference when dependencies change
    startTimeRef.current = null;
    prevCountRef.current = direction === "up" ? from : to;
    setCount(direction === "up" ? from : to);
    
    // Cancel any existing animation
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    
    const startValue = direction === "up" ? from : to;
    const endValue = direction === "up" ? to : from;
    const totalChange = endValue - startValue;
    
    // Easing function (easeOutExpo)
    const easeOutExpo = (t) => {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    };
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }
      
      const elapsedTime = timestamp - startTimeRef.current;
      const progress = Math.min(elapsedTime / (duration * 1000), 1);
      const easedProgress = easeOutExpo(progress);
      
      const currentValue = startValue + (totalChange * easedProgress);
      setCount(currentValue);
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [from, to, duration, direction, inView]);
  
  return (
    <span ref={ref} className={className}>
      {formatNumber(count)}
    </span>
  );
};

export default CountUp; 